import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  useAdForgeApproveCreativeMutation,
  useAdForgeRequestChangesCreativeMutation,
} from "~/ui-rtk/api/adForgeApi"
import {
  AgendaSlotDto,
  AgendaSlotLinkDto,
  AgendaSlotStatus,
  AgendaSlotType,
  useAgendaControllerCreateMutation,
  useAgendaControllerDeleteMutation,
  useAgendaControllerUpdateLinkMutation,
  useAgendaControllerUpdateMutation,
} from "~/ui-rtk/api/agendaApi"
import { useKeyEventControllerCreateKeyEventMutation } from "~/ui-rtk/api/keyEventsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button, Input, Select } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  LINK_STATUS_META,
  SLOT_STATUS_OPTIONS,
  SLOT_TYPE_META,
  SLOT_TYPE_OPTIONS,
} from "../constants"
import { slotProgress } from "../utils"
import AssigneePicker from "./AssigneePicker"
import LinkJobsPicker, { SLOT_JOB_CONFIG } from "./LinkJobsPicker"

/**
 * What the create wizard can produce: an AgendaSlot of any type, or a company
 * KeyEvent (product launch, promo…) — created through the key-events API and
 * shown on the calendar as the read-only amber overlay.
 */
type WizardType = AgendaSlotType | "KEY_EVENT"

type FormState = {
  title: string
  type: WizardType
  status: AgendaSlotStatus
  scheduledDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD or ""
  quantity: string // positive integer or ""
  description: string
  assigneeId: string | null
  targetUrl: string // GENERIC only — drives the "Open link" action
  keyEventType: string // KEY_EVENT only — e.g. "Product launch"
  spend: string // KEY_EVENT only — planned spend, optional
}

const STEPS = [
  {
    label: "Type",
    title: "What kind of work is this?",
    description:
      "The type decides which flow the slot opens and how it completes.",
  },
  {
    label: "Details",
    title: "When and what",
    description: "Give the slot a name, a date, and a target to work toward.",
  },
  {
    label: "Assign",
    title: "Status & owner",
    description: "Pick who's responsible and where this stands right now.",
  },
] as const

/** Per-type copy for the type-selection cards. */
const TYPE_DESCRIPTION: Record<WizardType, string> = {
  AD_BLAST: "Schedule ads to blast into ad sets",
  AD_CREATIVE: "Plan creatives to produce and approve",
  BUDGET_RECOMMENDATION: "Plan a budget review session",
  GENERIC: "Any other task, optionally with a link",
  KEY_EVENT: "Company milestone — product launch, promo, sale…",
}

const WIZARD_TYPES = Object.keys(TYPE_DESCRIPTION) as WizardType[]

function wizardTypeLabel(type: WizardType): string {
  return type === "KEY_EVENT" ? "Key Event" : SLOT_TYPE_META[type].label
}

/** Per-type label for the quantity field; null hides the field. */
const QUANTITY_LABEL: Record<WizardType, string | null> = {
  AD_BLAST: "Ads to blast",
  AD_CREATIVE: "Creatives to produce",
  BUDGET_RECOMMENDATION: null,
  GENERIC: "Quantity (optional)",
  KEY_EVENT: null,
}

function emptyForm(defaultDate: string): FormState {
  return {
    title: "",
    type: "AD_BLAST",
    status: "TODO",
    scheduledDate: defaultDate,
    endDate: "",
    quantity: "",
    description: "",
    assigneeId: null,
    targetUrl: "",
    keyEventType: "",
    spend: "",
  }
}

function FieldLabel({
  children,
  hint,
}: {
  children: React.ReactNode
  hint?: string
}) {
  return (
    <span className="flex items-baseline justify-between">
      <span className="text-sm font-medium text-system-gray-300">
        {children}
      </span>
      {hint && <span className="text-xs text-system-gray-500">{hint}</span>}
    </span>
  )
}

function StepHeading({ index }: { index: number }) {
  const step = STEPS[index]
  return (
    <div>
      <h4 className="flex items-center gap-2 text-base font-semibold text-white">
        <span className="h-5 w-1 rounded-full bg-system-brand-pink-500" />
        {step.title}
      </h4>
      <p className="mt-1 text-sm text-system-gray-400">{step.description}</p>
    </div>
  )
}

export default function SlotDialog({
  isOpen,
  slot,
  defaultDate,
  onClose,
  onAction,
}: {
  isOpen: boolean
  slot: AgendaSlotDto | null
  defaultDate: string // YYYY-MM-DD used when creating from a day cell
  onClose: () => void
  onAction: (slot: AgendaSlotDto) => void
}) {
  const dayjs = useDate()
  const [form, setForm] = useState<FormState>(emptyForm(defaultDate))
  const [step, setStep] = useState(0)
  const [searchParams, setSearchParams] = useSearchParams()

  // Opens the globally-mounted JobDetailModal (JobDetailModalHost in the
  // Topbar reacts to `?job=<id>`); it stacks on top of this dialog.
  const openJobDetail = (jobRunId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set("job", jobRunId)
    setSearchParams(next)
  }

  const [createSlot, { isLoading: isCreating }] =
    useAgendaControllerCreateMutation()
  const [updateSlot, { isLoading: isUpdating }] =
    useAgendaControllerUpdateMutation()
  const [deleteSlot, { isLoading: isDeleting }] =
    useAgendaControllerDeleteMutation()
  const [updateLink, { isLoading: isReviewingLink }] =
    useAgendaControllerUpdateLinkMutation()
  const [createKeyEvent, { isLoading: isCreatingKeyEvent }] =
    useKeyEventControllerCreateKeyEventMutation()

  useEffect(() => {
    if (!isOpen) return
    setStep(0)
    if (slot) {
      setForm({
        title: slot.title,
        type: slot.type,
        status: slot.status,
        scheduledDate: dayjs(slot.scheduledDate).format("YYYY-MM-DD"),
        endDate: slot.endDate ? dayjs(slot.endDate).format("YYYY-MM-DD") : "",
        quantity: slot.quantity ? String(slot.quantity) : "",
        description: slot.description ?? "",
        assigneeId: slot.assigneeId ?? null,
        targetUrl: slot.targetUrl ?? "",
        keyEventType: "",
        spend: "",
      })
    } else {
      setForm(emptyForm(defaultDate))
    }
    // Re-init only when the dialog opens or switches slots (slot?.id, not the
    // object) — a background refetch of the same slot (e.g. after a link
    // approval) must not clobber in-progress edits.
  }, [isOpen, slot?.id, defaultDate, dayjs])

  // Linking/approving work can recompute the slot's status server-side while
  // the dialog is open (e.g. 4/4 delivered → DONE). Mirror that into the form
  // so a subsequent "Save changes" doesn't send the stale status back and
  // silently revert the auto-transition. Fires only on real server-side
  // changes, so manual (unsaved) selections in the Status field are kept.
  const serverStatus = slot?.status
  useEffect(() => {
    if (!isOpen || !serverStatus) return
    setForm(prev =>
      prev.status === serverStatus ? prev : { ...prev, status: serverStatus },
    )
  }, [isOpen, serverStatus])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  // Create runs through the 3-step wizard; editing an existing slot shows
  // everything at once — a person clicking a calendar chip wants to tweak one
  // field or review linked work, not re-answer the wizard.
  const isEdit = Boolean(slot)
  const isKeyEvent = form.type === "KEY_EVENT"
  // Key events have no status/assignee — their wizard ends at Details.
  const stepCount = isKeyEvent ? 2 : STEPS.length
  const isSaving = isCreating || isUpdating || isCreatingKeyEvent
  const isLastStep = step === stepCount - 1

  const validateDetails = () => {
    if (!form.title.trim()) {
      toast.error(isKeyEvent ? "Name is required" : "Title is required")
      return false
    }
    if (isKeyEvent && !form.keyEventType.trim()) {
      toast.error("Event type is required (e.g. Product launch)")
      return false
    }
    const quantity = parseInt(form.quantity, 10)
    if (form.quantity && (!Number.isInteger(quantity) || quantity < 1)) {
      toast.error("Quantity must be a positive number")
      return false
    }
    return true
  }

  const goNext = () => {
    if (step === 1 && !validateDetails()) return
    setStep(current => Math.min(current + 1, stepCount - 1))
  }

  const handleSubmit = async () => {
    if (!validateDetails()) {
      if (!isEdit) setStep(1)
      return
    }

    // Key events are their own entity — created through the key-events API
    // and rendered as the read-only amber overlay on the calendar.
    if (isKeyEvent) {
      try {
        await createKeyEvent({
          createKeyEventDto: {
            name: form.title.trim(),
            type: form.keyEventType.trim(),
            spend: form.spend ? Math.round(parseFloat(form.spend)) : undefined,
            start: dayjs(form.scheduledDate).startOf("day").toDate(),
            end: form.endDate
              ? dayjs(form.endDate).endOf("day").toDate()
              : undefined,
          },
        }).unwrap()
        toast.success("Key event created")
        onClose()
      } catch {
        toast.error("Failed to create key event")
      }
      return
    }

    const quantity = parseInt(form.quantity, 10)
    const payload = {
      title: form.title.trim(),
      type: form.type as AgendaSlotType,
      status: form.status,
      description: form.description.trim() || undefined,
      // null clears a previously set quantity on edit
      quantity: form.quantity ? quantity : null,
      scheduledDate: dayjs(form.scheduledDate).startOf("day").toISOString(),
      endDate: form.endDate
        ? dayjs(form.endDate).endOf("day").toISOString()
        : undefined,
      assigneeId: form.assigneeId ?? undefined,
      ...(form.type === "GENERIC" && form.targetUrl.trim()
        ? { targetUrl: form.targetUrl.trim() }
        : {}),
    }

    try {
      if (slot) {
        await updateSlot({
          id: slot.id,
          updateAgendaSlotDto: payload,
        }).unwrap()
        toast.success("Slot updated")
      } else {
        await createSlot({ createAgendaSlotDto: payload }).unwrap()
        toast.success("Slot created")
      }
      onClose()
    } catch {
      toast.error("Failed to save slot")
    }
  }

  const handleDelete = async () => {
    if (!slot) return
    try {
      await deleteSlot({ id: slot.id }).unwrap()
      toast.success("Slot deleted")
      onClose()
    } catch {
      toast.error("Failed to delete slot")
    }
  }

  // Inline "request changes" comment box state (one open at a time).
  const [changesForLink, setChangesForLink] = useState<string | null>(null)
  const [changeNote, setChangeNote] = useState("")
  const [approveCreative] = useAdForgeApproveCreativeMutation()
  const [requestChangesCreative] = useAdForgeRequestChangesCreativeMutation()

  const approveLinkItem = async (link: AgendaSlotLinkDto) => {
    if (!slot) return
    try {
      if (link.adCreativeId) {
        // Best-effort sync of the creative's own approval state — a 409 (e.g.
        // still a draft, or already reviewed) must not block the slot review.
        await approveCreative({ id: link.adCreativeId })
          .unwrap()
          .catch(() => undefined)
      }
      await updateLink({
        id: slot.id,
        linkId: link.id,
        updateAgendaSlotLinkDto: { status: "APPROVED" },
      }).unwrap()
      toast.success("Item approved")
    } catch {
      toast.error("Failed to approve item")
    }
  }

  const submitChangeRequest = async (link: AgendaSlotLinkDto) => {
    if (!slot) return
    const note = changeNote.trim()
    if (!note) {
      toast.error("Add a short note about what should change")
      return
    }
    try {
      if (link.adCreativeId) {
        // Mirrors the public review page: the note lands on the creative
        // (reviewNote + CHANGES_REQUESTED) when it is awaiting review.
        await requestChangesCreative({ id: link.adCreativeId, note })
          .unwrap()
          .catch(() => undefined)
      }
      await updateLink({
        id: slot.id,
        linkId: link.id,
        updateAgendaSlotLinkDto: { status: "REJECTED", note },
      }).unwrap()
      toast.success("Changes requested")
      setChangesForLink(null)
      setChangeNote("")
    } catch {
      toast.error("Failed to request changes")
    }
  }

  const renderStepper = () => (
    <div className="flex items-center">
      {STEPS.slice(0, stepCount).map((stepMeta, index) => {
        const isDone = index < step
        const isCurrent = index === step
        return (
          <div
            key={stepMeta.label}
            className={cn("flex items-center", index > 0 && "flex-1")}
          >
            {index > 0 && (
              <span
                className={cn(
                  "mx-2 h-px flex-1 transition-colors duration-200",
                  isDone || isCurrent
                    ? "bg-system-brand-pink-500/60"
                    : "bg-system-blue-600/40",
                )}
              />
            )}
            <button
              type="button"
              className="group flex items-center gap-2"
              onClick={() => {
                // Free navigation backward; one step forward passes validation.
                if (index < step) setStep(index)
                else if (index === step + 1) goNext()
              }}
            >
              <span
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-all duration-200",
                  isCurrent &&
                    "bg-system-brand-pink-500 text-white shadow-[0_0_10px_rgba(236,72,153,0.4)]",
                  isDone &&
                    "bg-system-brand-pink-600/30 text-system-brand-pink-300",
                  !isCurrent &&
                    !isDone &&
                    "bg-system-blue-700/60 text-system-gray-500",
                )}
              >
                {isDone ? "✓" : index + 1}
              </span>
              <span
                className={cn(
                  "text-xs font-medium transition-colors duration-200",
                  isCurrent && "text-white",
                  isDone && "text-system-brand-pink-300",
                  !isCurrent &&
                    !isDone &&
                    "text-system-gray-500 group-hover:text-system-gray-300",
                )}
              >
                {stepMeta.label}
              </span>
            </button>
          </div>
        )
      })}
    </div>
  )

  const renderTypeStep = () => (
    <div className="flex flex-col gap-2">
      {WIZARD_TYPES.map(type => {
        const isSelected = form.type === type
        return (
          <button
            key={type}
            type="button"
            onClick={() => set("type", type)}
            className={cn(
              "flex items-center gap-3 rounded-xl border p-4 text-left transition-all duration-200",
              "bg-system-blue-800/60",
              isSelected
                ? "border-system-brand-pink-500/60 shadow-[0_0_12px_rgba(236,72,153,0.15)]"
                : "border-system-blue-600/40 hover:border-system-blue-500/60",
            )}
          >
            <span
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all duration-200",
                isSelected
                  ? "border-system-brand-pink-500"
                  : "border-system-gray-500",
              )}
            >
              {isSelected && (
                <span className="h-2 w-2 rounded-full bg-system-brand-pink-500" />
              )}
            </span>
            <span className="flex flex-col">
              <span
                className={cn(
                  "flex items-center gap-1.5 text-sm font-semibold transition-colors",
                  isSelected ? "text-white" : "text-system-gray-300",
                )}
              >
                {type === "KEY_EVENT" && (
                  <span className="text-amber-400">★</span>
                )}
                {wizardTypeLabel(type)}
              </span>
              <span className="text-xs text-system-gray-400">
                {TYPE_DESCRIPTION[type]}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )

  const renderDetailsStep = () => (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <FieldLabel>{isKeyEvent ? "Name" : "Title"}</FieldLabel>
        <Input
          fullWidth
          value={form.title}
          onChange={e => set("title", e.target.value)}
          placeholder={
            isKeyEvent
              ? "e.g. Summer collection launch"
              : "e.g. Blast 4 ads Monday"
          }
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5">
          <FieldLabel>Scheduled date</FieldLabel>
          <Input
            fullWidth
            type="date"
            value={form.scheduledDate}
            onChange={e => set("scheduledDate", e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <FieldLabel hint="optional">End date</FieldLabel>
          <Input
            fullWidth
            type="date"
            value={form.endDate}
            min={form.scheduledDate}
            onChange={e => set("endDate", e.target.value)}
          />
        </label>
      </div>

      {isKeyEvent ? (
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1.5">
            <FieldLabel>Event type</FieldLabel>
            <Input
              fullWidth
              value={form.keyEventType}
              placeholder="e.g. Product launch"
              onChange={e => set("keyEventType", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <FieldLabel hint="optional">Planned spend ($)</FieldLabel>
            <Input
              fullWidth
              type="number"
              min={0}
              placeholder="e.g. 5000"
              value={form.spend}
              onChange={e => set("spend", e.target.value)}
            />
          </label>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            {QUANTITY_LABEL[form.type] && (
              <label className="flex flex-col gap-1.5">
                <FieldLabel hint="sets the progress target">
                  {QUANTITY_LABEL[form.type]}
                </FieldLabel>
                <Input
                  fullWidth
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 4"
                  value={form.quantity}
                  onChange={e => set("quantity", e.target.value)}
                />
              </label>
            )}
            {form.type === "GENERIC" && (
              <label className="flex flex-col gap-1.5">
                <FieldLabel hint="optional">Target URL</FieldLabel>
                <Input
                  fullWidth
                  value={form.targetUrl}
                  placeholder="https://…"
                  onChange={e => set("targetUrl", e.target.value)}
                />
              </label>
            )}
          </div>

          <label className="flex flex-col gap-1.5">
            <FieldLabel hint="optional">Notes</FieldLabel>
            <textarea
              className="textarea textarea-bordered w-full rounded-lg border-basic-blue bg-basic-dark-blue text-sm leading-relaxed placeholder:text-system-gray-500 focus:border-system-brand-pink-500/60 focus:outline-none"
              rows={3}
              placeholder="Anything the owner should know…"
              value={form.description}
              onChange={e => set("description", e.target.value)}
            />
          </label>
        </>
      )}
    </div>
  )

  const renderAssignStep = () => (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5">
          <FieldLabel>Status</FieldLabel>
          <Select
            options={SLOT_STATUS_OPTIONS}
            value={form.status}
            onChange={e => set("status", e.target.value as AgendaSlotStatus)}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <FieldLabel>Assignee</FieldLabel>
          <AssigneePicker
            value={form.assigneeId}
            onChange={id => set("assigneeId", id)}
          />
        </label>
      </div>

      {slot && SLOT_JOB_CONFIG[slot.type] && (
        <div className="rounded-xl border border-system-blue-600/40 bg-system-blue-800/40 p-4">
          <FieldLabel hint="ran inside the slot's dates">
            Link {SLOT_JOB_CONFIG[slot.type]?.noun}
          </FieldLabel>
          <div className="mt-2">
            <LinkJobsPicker slot={slot} />
          </div>
        </div>
      )}

      {slot && slot.links.length > 0 && (
        <div className="rounded-xl border border-system-blue-600/40 bg-system-blue-800/40 p-4">
          <FieldLabel
            hint={(() => {
              const progress = slotProgress(slot)
              return progress
                ? `${progress.completed}/${progress.target} done`
                : undefined
            })()}
          >
            Linked work
          </FieldLabel>
          <div className="mt-2 flex max-h-56 flex-col gap-1.5 overflow-y-auto">
            {slot.links.map(link => (
              <div
                key={link.id}
                className="flex flex-col rounded-lg border border-system-blue-600/30 bg-system-blue-800/60 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className={cn(
                        "badge badge-xs shrink-0",
                        LINK_STATUS_META[link.status].badgeClass,
                      )}
                    >
                      {LINK_STATUS_META[link.status].label}
                    </span>
                    <span className="truncate text-xs text-system-gray-300">
                      {link.units} unit{link.units > 1 ? "s" : ""}
                      {link.jobRunId
                        ? ` · blast job ${link.jobRunId.slice(0, 8)}…`
                        : ""}
                      {link.adCreativeId
                        ? ` · creative ${link.adCreativeId.slice(0, 8)}…`
                        : ""}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {link.jobRunId && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        title="View job details"
                        aria-label="View job details"
                        onClick={() => openJobDetail(link.jobRunId as string)}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {slot.type === "AD_CREATIVE" &&
                      link.status === "DELIVERED" && (
                        <>
                          <button
                            type="button"
                            className="btn btn-success btn-xs"
                            disabled={isReviewingLink}
                            onClick={() => approveLinkItem(link)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs text-states-warning"
                            disabled={isReviewingLink}
                            onClick={() => {
                              setChangesForLink(
                                changesForLink === link.id ? null : link.id,
                              )
                              setChangeNote("")
                            }}
                          >
                            Request changes
                          </button>
                        </>
                      )}
                  </div>
                </div>

                {link.note && (
                  <p className="mt-1 text-xs italic text-system-gray-400">
                    “{link.note}”
                  </p>
                )}

                {changesForLink === link.id && (
                  <div className="mt-2 flex flex-col gap-2">
                    <textarea
                      className="textarea textarea-bordered w-full rounded-lg border-basic-blue bg-basic-dark-blue text-xs leading-relaxed placeholder:text-system-gray-500 focus:border-system-brand-pink-500/60 focus:outline-none"
                      rows={2}
                      autoFocus
                      placeholder="What should change? The note is sent to the creative's review thread."
                      value={changeNote}
                      onChange={e => setChangeNote(e.target.value)}
                    />
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() => {
                          setChangesForLink(null)
                          setChangeNote("")
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-xs"
                        disabled={isReviewingLink || !changeNote.trim()}
                        onClick={() => submitChangeRequest(link)}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  const STEP_CONTENT = [renderTypeStep, renderDetailsStep, renderAssignStep]

  let dialogTitle = "New slot"
  if (isEdit) dialogTitle = "Edit slot"
  else if (isKeyEvent) dialogTitle = "New key event"

  let submitLabel = "Create slot"
  if (isEdit) submitLabel = "Save changes"
  else if (isKeyEvent) submitLabel = "Create key event"

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={560}
      closeOnClickOutside
      classes={{
        container:
          "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl",
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{dialogTitle}</h3>
          <span
            className={cn(
              "badge badge-sm",
              isKeyEvent
                ? "border-amber-500/60 bg-amber-500/20 text-amber-300"
                : SLOT_TYPE_META[form.type as AgendaSlotType].badgeClass,
            )}
          >
            {wizardTypeLabel(form.type)}
          </span>
        </div>

        {isEdit ? (
          <div className="flex max-h-[65vh] flex-col gap-4 overflow-y-auto pr-1 animate-fadeIn">
            <label className="flex flex-col gap-1.5">
              <FieldLabel>Type</FieldLabel>
              <Select
                options={SLOT_TYPE_OPTIONS}
                value={form.type}
                onChange={e => set("type", e.target.value as AgendaSlotType)}
              />
            </label>
            {renderDetailsStep()}
            {renderAssignStep()}
          </div>
        ) : (
          <>
            {renderStepper()}

            <div key={step} className="min-h-[280px] animate-fadeIn">
              <div className="mb-4">
                <StepHeading index={step} />
              </div>
              {STEP_CONTENT[step]()}
            </div>
          </>
        )}

        <div className="flex items-center justify-between border-t border-system-blue-600/30 pt-4">
          <div className="flex items-center gap-1">
            {slot && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm text-states-error"
                  disabled={isDeleting}
                  onClick={handleDelete}
                >
                  Delete
                </button>
                {(slot.type !== "GENERIC" || slot.targetUrl) && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm text-system-brand-pink-400 hover:text-system-brand-pink-300"
                    onClick={() => {
                      onAction(slot)
                      onClose()
                    }}
                  >
                    {SLOT_TYPE_META[slot.type].actionLabel}
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={{ variant: "text" }}
              size="sm"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            {!isEdit && step > 0 && (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                size="sm"
                type="button"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
            )}
            {!isEdit && !isLastStep ? (
              <Button
                variant={{ variant: "solid", color: "pink" }}
                size="sm"
                type="button"
                onClick={goNext}
              >
                Next
              </Button>
            ) : (
              <Button
                variant={{ variant: "solid", color: "pink" }}
                size="sm"
                type="button"
                isLoading={isSaving}
                disabled={isSaving}
                onClick={handleSubmit}
              >
                {submitLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  )
}
